import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { formatMoneyWithCurrencyCode } from '../utils/formatMoneyWithCurrencyCode';
import { DashboardAccountSummaryData } from '../types/myaccount';
import MyAccountCard from './MyAccountCard';

interface AccountSummarySettings {
    title?: string;
    showOrdersBalance?: boolean;
    showCreditLimit?: boolean;
    showRemainingCredit?: boolean;
    showOverdueDays?: boolean;
    showProgressBar?: boolean;
    highlightOverdue?: boolean;
}

interface AccountSummaryProps {
    settings?: AccountSummarySettings;
    defaults?: AccountSummarySettings;
}

const baseSettings: Required<AccountSummarySettings> = {
    title: 'Account Summary',
    showOrdersBalance: true,
    showCreditLimit: true,
    showRemainingCredit: true,
    showOverdueDays: true,
    showProgressBar: false,
    highlightOverdue: true,
};

const mergeDefinedSettings = (...sources: AccountSummarySettings[]): AccountSummarySettings => {
    const merged: AccountSummarySettings = {};

    sources.forEach((source) => {
        Object.entries(source || {}).forEach(([key, value]) => {
            if (value !== undefined) {
                merged[key as keyof AccountSummarySettings] = value as never;
            }
        });
    });

    return merged;
};

const AccountSummary: React.FC<AccountSummaryProps> = ({
    settings = {},
    defaults = {},
}) => {
    const [data, setData] = useState<DashboardAccountSummaryData | null>(null);
    const [loading, setLoading] = useState(true);
    const mergedSettings = mergeDefinedSettings(defaults, settings);

    const resolvedSettings: Required<AccountSummarySettings> = {
        ...baseSettings,
        ...mergedSettings,
        title: typeof settings.title === 'string' && settings.title.trim() !== ''
            ? settings.title.trim()
            : (typeof defaults.title === 'string' && defaults.title.trim() !== ''
                ? defaults.title.trim()
                : baseSettings.title),
    };

    useEffect(() => {
        const loadAccountSummary = async () => {
            try {
                const dashboardData = await api.getAccountDashboardData();
                setData(dashboardData.account_summary || null);
            } catch (error) {
                console.error('Error loading account summary:', error);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        loadAccountSummary();
    }, []);

    const formatCurrency = (amount: number) => {
        return formatMoneyWithCurrencyCode(
            amount,
            data?.currency_code || 'AUD',
            'symbol',
            data?.currency_symbol,
            'en-AU'
        );
    };

    const metricCards = loading ? [] : [
        {
            key: 'orders_balance',
            enabled: resolvedSettings.showOrdersBalance,
            label: 'Orders Balance',
            value: data ? formatCurrency(data.orders_balance) : '',
            emphasized: false,
        },
        {
            key: 'credit_limit',
            enabled: resolvedSettings.showCreditLimit,
            label: 'Credit Limit',
            value: data ? formatCurrency(data.credit_limit) : '',
            emphasized: false,
        },
        {
            key: 'remaining_credit',
            enabled: resolvedSettings.showRemainingCredit,
            label: 'Remaining Credit',
            value: data ? formatCurrency(data.remaining_credit) : '',
            emphasized: false,
        },
        {
            key: 'overdue_days',
            enabled: resolvedSettings.showOverdueDays,
            label: 'Overdue Days',
            value: data ? `${data.overdue_days} day${data.overdue_days === 1 ? '' : 's'}` : '',
            emphasized: !!(data && resolvedSettings.highlightOverdue && data.overdue_days > 0),
        },
    ].filter((metric) => metric.enabled);

    const availableCreditPercent = data && data.credit_limit > 0
        ? Math.max(0, Math.min(100, (data.remaining_credit / data.credit_limit) * 100))
        : 0;
    const showWarning = !!(
        data
        && resolvedSettings.highlightOverdue
        && (data.on_hold || data.overdue_days > 0)
    );

    if (loading) {
        return (
            <MyAccountCard slug="account-summary" title={resolvedSettings.title} className="account-summary">
                <div className="summary-grid row g-3">
                    {Array.from({ length: 4 }).map((_, index) => (
                        <div className="col-12 col-md-6 col-xl-3" key={`account-summary-skeleton-${index}`}>
                            <div className="border p-3 h-100 account-summary__loading-card">
                                <div className="skeleton" style={{ height: '16px', width: '65%', marginBottom: '10px' }} />
                                <div className="skeleton" style={{ height: '30px', width: '80%' }} />
                            </div>
                        </div>
                    ))}
                </div>
            </MyAccountCard>
        );
    }

    if (!data) {
        return null;
    }

    return (
        <MyAccountCard
            slug="account-summary"
            title={resolvedSettings.title}
            className="account-summary"
            contentClassName="d-flex flex-column gap-3"
        >
            {showWarning && (
                <div
                    className="px-3 py-2 account-summary__warning"
                    style={{
                        background: 'var(--weka-danger-bg)',
                        border: '1px solid color-mix(in srgb, var(--weka-danger-color) 22%, var(--weka-card-bg) 78%)',
                        color: 'var(--weka-danger-color)',
                    }}
                >
                    {data.on_hold
                        ? 'Account hold is active. On-account checkout is restricted until the hold is cleared.'
                        : `This account is ${data.overdue_days} day${data.overdue_days === 1 ? '' : 's'} overdue.`}
                </div>
            )}

            {metricCards.length > 0 && (
                <div className="summary-grid row g-3">
                    {metricCards.map((metric) => (
                        <div className="col-12 col-md-6 col-xl-3" key={metric.key}>
                            <div
                                className="border p-3 h-100 d-flex flex-column account-summary__metric-card"
                                style={{
                                    background: metric.emphasized ? 'var(--weka-danger-bg)' : 'var(--weka-subtle-bg)',
                                    borderColor: metric.emphasized
                                        ? 'color-mix(in srgb, var(--weka-danger-color) 20%, var(--weka-card-bg) 80%)'
                                        : 'var(--weka-subtle-border-color)',
                                }}
                            >
                                <div className="text-uppercase small text-muted mb-2 account-summary__metric-label">{metric.label}</div>
                                <div
                                    className="fw-semibold mt-auto pt-3 account-summary__metric-value"
                                    style={{
                                        fontSize: '1.35rem',
                                        color: metric.emphasized ? 'var(--weka-danger-color)' : 'var(--weka-heading-color)',
                                    }}
                                >
                                    {metric.value}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {resolvedSettings.showProgressBar && data.credit_limit > 0 && (
                <div className="border p-3 account-summary__progress" style={{ background: 'var(--weka-subtle-bg)', borderColor: 'var(--weka-subtle-border-color)' }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small text-uppercase text-muted">Available Credit</span>
                        <span className="fw-semibold">{Math.round(availableCreditPercent)}%</span>
                    </div>
                    <div className="progress" style={{ height: '10px', background: 'var(--weka-subtle-border-color)' }}>
                        <div
                            className="progress-bar"
                            role="progressbar"
                            style={{
                                width: `${availableCreditPercent}%`,
                                background: data.on_hold || data.overdue_days > 0 ? 'var(--weka-danger-color)' : 'var(--weka-info-color)',
                            }}
                            aria-valuenow={availableCreditPercent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        />
                    </div>
                    <div className="d-flex justify-content-between mt-2 small text-muted">
                        <span>{formatCurrency(data.remaining_credit)} available</span>
                        <span>{formatCurrency(data.credit_limit)} total</span>
                    </div>
                </div>
            )}

            {metricCards.length === 0 && !resolvedSettings.showProgressBar && (
                <div className="text-muted small">
                    No account summary metrics are enabled for this widget.
                </div>
            )}
        </MyAccountCard>
    );
};

export default AccountSummary;
